import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import S3FileUploader from './S3FileUploader';
import { Button, Image, Spin, Upload } from 'antd';
import { UploadChangeParam } from 'antd/lib/upload';
import ImgCrop, { ImgCropProps } from './AntdImgCrop';
import { CloseOutlined, InboxOutlined } from '@ant-design/icons';
import { useConnection } from '@sezenta/connection';
import { useTranslation } from 'react-i18next';

type Props = {
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  multiple?: boolean;
  image?: Omit<ImgCropProps, 'children'> & {
    crop?: boolean;
    width?: number;
    height?: number;
  };
};

const UploadImagesPreview: FC<{
  values: string[];
  width: number;
  height: number;
  onChange: (deleted: string, values?: string[]) => void;
}> = (props) => {
  return (
    <div className="flex flex-wrap w-full gap-4 px-4">
      {props.values.map((v, i) => (
        <div
          key={v}
          className="w-fit relative bg-white border border-solid border-slate-300 p-2"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Image
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            src={v}
            wrapperStyle={{
              width: props.width,
              height: props.height,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            style={{
              width: 'auto',
              height: 'auto',
              maxWidth: props.width,
              maxHeight: props.height,
            }}
          />
          <Button
            onClick={() => {
              const res = props.values.filter((_, j) => i !== j);
              props.onChange(v, res.length === 0 ? undefined : res);
            }}
            size="small"
            danger
            shape="circle"
            type="default"
            style={{
              position: 'absolute',
              fontSize: 10,
              width: 30,
              height: 30,
              minWidth: 30,
              top: -15,
              right: -15,
            }}
          >
            <CloseOutlined />
          </Button>
        </div>
      ))}
    </div>
  );
};

const UploadFilesPreview: FC<{
  values: string[];
  onChange: (deleted: string, values?: string[]) => void;
}> = (props) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 w-full gap-4 px-4">
      {props.values.map((v, i) => (
        <div
          key={v}
          className="relative bg-white border border-solid border-slate-300 p-2"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div className="truncate">{v.substring(v.lastIndexOf('/') + 1)}</div>
          <Button
            onClick={() => {
              const res = props.values.filter((_, j) => i !== j);
              props.onChange(v, res.length === 0 ? undefined : res);
            }}
            size="small"
            danger
            shape="circle"
            type="default"
            style={{
              position: 'absolute',
              fontSize: 10,
              width: 30,
              height: 30,
              minWidth: 30,
              top: -15,
              right: -15,
            }}
          >
            <CloseOutlined />
          </Button>
        </div>
      ))}
    </div>
  );
};

export const UploadInput: FC<Props> = (props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const connection = useConnection();
  const s3Uploader = useRef(new S3FileUploader(connection, setLoading));
  const [deleted, setDeleted] = useState<string[]>([]);
  const { t } = useTranslation();

  const onChange = useCallback(
    (info: UploadChangeParam) => {
      if (info.fileList.filter((v) => v.status !== 'done').length === 0) {
        if (props.multiple) {
          const val = [...(props.value ?? [])];
          info.fileList
            .map((f) => s3Uploader.current.getUrl(f.uid))
            .filter((f) => !val.includes(f) && !deleted.includes(f))
            .forEach((f) => val.push(f));
          props.onChange?.(val);
        } else {
          props.onChange?.(
            info.fileList.map((f) => s3Uploader.current.getUrl(f.uid))[
              info.fileList.length - 1
            ],
          );
        }

        setLoading(false);
      } else if (
        info.fileList.filter((v) => v.status === 'uploading').length === 0
      ) {
        setLoading(false);
      }
    },
    [deleted, props],
  );

  const values: string[] = useMemo(() => {
    if (props.value === undefined) {
      return [];
    }
    return Array.isArray(props.value) ? props.value : [props.value];
  }, [props.value]);

  const onDeleted = useCallback(
    (del: string, vx?: string[]) => {
      setDeleted([...deleted, del]);
      props.onChange?.(vx as any);
    },
    [deleted, props],
  );

  const [w, h] = useMemo(() => {
    const width = props.image?.width ?? 128;
    const height = props.image?.height ?? 128;
    if (width > 128) {
      return [128, (128 / width) * height];
    }
    return [width, height];
  }, [props.image?.height, props.image?.width]);

  const Uploader = useCallback(
    () => (
      <Upload.Dragger
        showUploadList={false}
        multiple={props.multiple}
        onChange={onChange}
        customRequest={s3Uploader.current?.upload}
      >
        <Spin spinning={loading}>
          {props.image ? (
            <UploadImagesPreview
              onChange={onDeleted}
              values={values}
              width={w}
              height={h}
            />
          ) : (
            <UploadFilesPreview onChange={onDeleted} values={values} />
          )}
          <div className="p-8">
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{t('uploadTitle')}</p>
            <p className="ant-upload-hint">{t('uploadDescription')}</p>
          </div>
        </Spin>
      </Upload.Dragger>
    ),
    [
      h,
      loading,
      onChange,
      onDeleted,
      props.image,
      props.multiple,
      t,
      values,
      w,
    ],
  );
  if (props.multiple !== true && values.length > 0) {
    return (
      <>
        {props.image ? (
          <UploadImagesPreview
            onChange={onDeleted}
            values={values}
            width={props.image.width ?? 100}
            height={props.image.height ?? 100}
          />
        ) : (
          <UploadFilesPreview onChange={onDeleted} values={values} />
        )}
      </>
    );
  }
  if (props.image?.crop) {
    return (
      <ImgCrop
        rotate={true}
        shape={'rect'}
        minZoom={0.2}
        // beforeCrop={checkFile}
        cropperProps={{ restrictPosition: false, objectFit: 'contain' }}
        {...props.image}
      >
        {Uploader()}
      </ImgCrop>
    );
  } else {
    return Uploader();
  }
};
